import { Injectable } from '@angular/core';
import * as CookieConsent from 'vanilla-cookieconsent';
import { AnalyticsService } from './analytics.service';
import { WebsiteStateService } from '../state-management/services/website-state.service';

@Injectable({
  providedIn: 'root'
})
export class ConsentManagementService {

  private CookieConsent = CookieConsent;

  constructor(
    private websiteStateService: WebsiteStateService,
    private analyticsService: AnalyticsService) { }

  public initCookieConsent(): void {
    CookieConsent.run(this._cookieConfig); // https://cookieconsent.orestbida.com/essential/getting-started.html#angular
  }

  public showPreferencesModal(): void {
    this.CookieConsent.showPreferences();
  }

  private _cookieConfig: CookieConsent.CookieConsentConfig = {
    onConsent: () => {
      if (CookieConsent.acceptedCategory('analytics')) {
        console.debug('User has accepted the cookie policy for analytics');
        this.analyticsService.initializeAnalytics();
      }

      if (CookieConsent.acceptedCategory('necessary')) {
        console.debug('User has accepted the cookie policy for necessary cookies');
        this.websiteStateService.setCCAccepted(true);

      }

      // Dummy code in case we want to also enable specific services for each category in the future 
      // if (CookieConsent.acceptedService('Google Analytics', 'analytics')) {
      // }
    },

    onChange: ({ cookie, changedCategories, changedServices }: { cookie: any, changedCategories: any, changedServices: any }) => {
      for (const category of changedCategories) {
        if (category === 'analytics') {
          if (this.CookieConsent.acceptedCategory('analytics')) {
            console.debug('User has accepted the cookie policy for analytics');
            this.analyticsService.activateAnalytics();
          } else {
            console.debug('User has not accepted the cookie policy for analytics');
            this.analyticsService.deactivateAnalytics();
          }
        }
      }
    },
    autoClearCookies: true,
    categories: {
      necessary: {
        enabled: true,  // this category is enabled by default
        readOnly: true  // this category cannot be disabled
      },
      analytics: {
        enabled: false,  // this category is disabled by default
        readOnly: false,  // this category can be enabled/disabled by the user
        autoClear: {
          cookies: [
            {
              name: /^(_ga)/      //regex
            },
            {
              name: '_gid'        //string
            }
          ]
        }

      }
    },
    disablePageInteraction: true,
    guiOptions: {
      preferencesModal: {
        flipButtons: false,
        equalWeightButtons: false,
      },
      consentModal: {
        flipButtons: false,
        equalWeightButtons: false,
      },
    },
    language: {
      default: 'de',
      translations: {
        de: {
          consentModal: {
            title: 'Datenschutz-Präferenz',
            description: 'Wir benötigen Ihre Einwilligung, bevor Sie unsere Website weiter besuchen können.',
            acceptAllBtn: 'Alle akzeptieren',
            acceptNecessaryBtn: 'Nur notwendige Cookies akzeptieren',
            showPreferencesBtn: 'Individuelle Einstellungen',
            footer: `
            <a href="impressum" target="_blank">Impressum</a>
            <a href="datenschutz" target="_blank">Datenschutz</a>
        `,
          },
          preferencesModal: {
            title: 'Datenschutz-Präferenz',
            acceptAllBtn: 'Alle akzeptieren',
            acceptNecessaryBtn: 'Nur notwendige Cookies akzeptieren',
            savePreferencesBtn: 'Individuelle Einstellungen speichern',
            closeIconLabel: 'Schließen',
            sections: [
              {
                title: 'Wir benötigen Ihre Einwilligung',
                description: `Wir verwenden Cookies und andere Technologien auf unserer Website. 
                <br>
                Einige von ihnen sind essenziell, während andere uns helfen, 
                diese Website und Ihre Erfahrung zu verbessern. Personenbezogene Daten (z. B. IP-Adressen) können verarbeitet werden, z. B. für personalisierte Anzeigen und Inhalte 
                oder die Messung von Anzeigen und Inhalten. Weitere Informationen über die Verwendung Ihrer Daten finden Sie in unserer Datenschutzerklärung. 
                <br>
                Es besteht keine Verpflichtung, in die Verarbeitung Ihrer Daten einzuwilligen, um dieses Angebot zu nutzen. Sie können Ihre Auswahl jederzeit unter 
                Einstellungen widerrufen oder anpassen. Bitte beachten Sie, dass aufgrund individueller Einstellungen möglicherweise nicht alle Funktionen der Website verfügbar sind.
                <br>
                Hier finden Sie eine Übersicht über alle verwendeten Cookies. Sie können Ihre Einwilligung für ganze Kategorien geben und sich weitere Informationen zu den Kategorien anzeigen lassen.`
              },
              {
                title: 'Essenziell',
                description: 'Essenzielle Services ermöglichen grundlegende Funktionen und sind für das ordnungsgemäße Funktionieren der Website erforderlich.',
                linkedCategory: 'necessary'
              },
              {
                title: 'Statistik',
                description: 'Statistik-Cookies sammeln Nutzungsdaten, die uns Aufschluss darüber geben, wie unsere Besucher mit unserer Website umgehen.',
                linkedCategory: 'analytics'
              },
              {
                title: 'Weitere Informationen',
                description: 'Für detaillierte Informationen zu den einzelnen Cookies sowie zu den jeweiligen Verarbeitungszwecken, können Sie unsere <a href="/datenschutz">Datenschutzerklärung</a> einsehen.'
              }
            ]
          }
        }
      }
    }
  };

  get cookieConfig() {
    return this._cookieConfig;
  }
}
